package io.horion.auth.presentation.ui.pages.password

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.text.SpanText
import io.horion.auth.di.koinGet
import io.horion.auth.presentation.ui.components.auth.AuthButton
import io.horion.auth.presentation.ui.components.auth.AuthTextField
import io.horion.auth.presentation.ui.components.layouts.PageLayoutData
import io.horion.auth.presentation.ui.theme.AuthSpacing
import io.horion.auth.presentation.ui.theme.AuthTheme
import io.horion.auth.presentation.ui.theme.AuthTypography
import io.horion.auth.presentation.viewmodel.AuthViewModel
import io.horion.auth.utils.Validators
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.auth.api.models.domain.LoginMethod
import io.horion.auth.presentation.ui.styles.AuthContentStyle
import io.horion.auth.presentation.ui.styles.AuthTitleStyle
import io.horion.auth.presentation.ui.styles.PageWrapperStyle
import io.horion.auth.presentation.ui.theme.AuthColors
import io.horion.auth.domain.models.AppLanguage
import io.horion.auth.domain.models.Countries
import io.horion.auth.presentation.ui.pages.navigation.Screen
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initResetPasswordPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(AppLanguage.strings.pageTitleReset))
}

@OptIn(DelicateApi::class)
@Page("/reset-password")
@Layout("io.horion.auth.presentation.ui.components.layouts.PageLayout")
@Composable
fun resetPasswordPage() {
    val ctx = rememberPageContext()
    val colorMode = ColorMode.current
    val colors = AuthTheme.colors(colorMode)
    val authViewModel = koinGet<AuthViewModel>()
    val authUiState = authViewModel.uiState
    val strings = AppLanguage.strings

    LaunchedEffect(Unit){
        authViewModel.clear()
    }

    // Redirection après succès
    LaunchedEffect(authUiState.verifySucceed) {
        if (authUiState.verifySucceed) {
            // Attendre 2 secondes pour montrer le message de succès
            ctx.router.navigateTo(Screen.ForgetPassword.route)
        }
    }
    var activeMethod by remember { mutableStateOf(LoginMethod.EMAIL) }
    var otpSent by remember { mutableStateOf(authUiState.confirmSucceed) }
    var otpCooldown by remember { mutableStateOf(0) }
    val scope = remember { CoroutineScope(Dispatchers.Main) }

    val contactFilled = if (activeMethod == LoginMethod.EMAIL)
        authUiState.email.isNotBlank() && Validators.isValidEmail(authUiState.email)
    else
        authUiState.phone.isNotBlank() && Validators.isValidPhone(authUiState.phone)

    val otpComplete = otpSent && authUiState.code.length == 6

    val canSubmit = (authUiState.email.isNotBlank() && Validators.isValidEmail(authUiState.email) )
            || (authUiState.phone.isNotBlank() && Validators.isValidPhone(authUiState.phone))

    fun startCooldown() {
        otpSent = true
        scope.launch {
            otpCooldown = 60
            while (otpCooldown > 0) { delay(1000); otpCooldown-- }
        }
    }

    LaunchedEffect(activeMethod) {
        otpSent = false
        otpCooldown = 0
        authViewModel.clearErrors()
    }

    Box(modifier = PageWrapperStyle.toModifier(), contentAlignment = Alignment.TopCenter) {
        Column(modifier = AuthContentStyle.toModifier(), horizontalAlignment = Alignment.CenterHorizontally) {
            Box(modifier = Modifier.height(AuthSpacing.xl))

            if (!authUiState.resetPasswordSucceed) {

                SpanText(
                    strings.resetTitle,
                    modifier = AuthTitleStyle.toModifier().fontWeight(FontWeight.Bold).color(colors.onBackground)
                )
                Box(modifier = Modifier.height(4.px))
                SpanText(
                    strings.resetSubtitle,
                    modifier = Modifier.fontSize(AuthTypography.small).color(colors.mutedText)
                )
                Box(modifier = Modifier.height(AuthSpacing.xl))

                authUiState.authErrorMessage?.let { error ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth().padding(12.px).borderRadius(8.px)
                            .border(1.px, LineStyle.Solid, colors.error)
                            .styleModifier { property("background-color", "rgba(239,68,68,0.08)") }
                            .gap(8.px),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        FaCircleExclamation(modifier = Modifier.color(colors.error).fontSize(14.px))
                        SpanText(error, modifier = Modifier.fontSize(AuthTypography.small).color(colors.error))
                    }
                    Box(modifier = Modifier.height(AuthSpacing.md))
                }

                Row(
                    modifier = Modifier.fillMaxWidth().margin(bottom = 8.px),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        if (activeMethod == LoginMethod.EMAIL) strings.resetViaEmail else strings.resetViaPhone,
                        modifier = Modifier.fontSize(AuthTypography.small).fontWeight(FontWeight.Bold).color(colors.onBackground)
                    )
                    SpanText(
                        if (activeMethod == LoginMethod.EMAIL) strings.switchToPhone else strings.switchToEmail,
                        modifier = Modifier
                            .fontSize(AuthTypography.tiny).fontWeight(FontWeight.SemiBold)
                            .color(colors.primary).cursor(Cursor.Pointer)
                            .onClick {
                                authViewModel.clear()
                                activeMethod = if (activeMethod == LoginMethod.EMAIL) LoginMethod.PHONE else LoginMethod.EMAIL
                            }
                    )
                }
                Box(modifier = Modifier.height(AuthSpacing.lg))

                if (activeMethod == LoginMethod.EMAIL) {
                    EmailResetForm(
                        viewModel = authViewModel,
                        otpCooldown = otpCooldown,
                        contactFilled = contactFilled,
                        otpSent = otpSent,
                        otpComplete = otpComplete,
                        onSendOtp = { startCooldown() },
                        colors = colors
                    )
                } else {
                    PhoneResetForm(
                        viewModel = authViewModel,
                        otpCooldown = otpCooldown,
                        contactFilled = contactFilled,
                        otpSent = otpSent,
                        otpComplete = otpComplete,
                        onSendOtp = { startCooldown() },
                        colors = colors
                    )
                }

                Box(modifier = Modifier.height(AuthSpacing.xl))

                AuthButton(
                    text = strings.resetButton,
                    onClick = { authViewModel.forgotPassword() },
                    loading = authUiState.isLoading,
                    disabled = !canSubmit

                )

            } else {
                Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                    Box(modifier = Modifier.height(AuthSpacing.xxl))
                    Box(
                        modifier = Modifier.size(72.px).borderRadius(50.percent)
                            .styleModifier { property("background-color", "rgba(16,185,129,0.12)") }
                            .border(2.px, LineStyle.Solid, colors.success),
                        contentAlignment = Alignment.Center
                    ) {
                        FaCheck(modifier = Modifier.fontSize(28.px).color(colors.success))
                    }
                    Box(modifier = Modifier.height(AuthSpacing.lg))
                    SpanText(strings.resetSuccessTitle, modifier = AuthTitleStyle.toModifier().fontWeight(FontWeight.Bold).color(colors.onBackground))
                    Box(modifier = Modifier.height(AuthSpacing.sm))
                    SpanText(strings.resetSuccessSubtitle, modifier = Modifier.fontSize(AuthTypography.small).color(colors.mutedText))
                }
            }

            Box(modifier = Modifier.height(AuthSpacing.xl))
        }
    }
}

@Composable
private fun EmailResetForm(
    viewModel: AuthViewModel,
    otpCooldown: Int,
    contactFilled: Boolean,
    otpSent: Boolean,
    otpComplete: Boolean,
    onSendOtp: () -> Unit,
    colors: AuthColors
) {
    val uiState = viewModel.uiState
    val strings = AppLanguage.strings
    Column(modifier = Modifier.fillMaxWidth().gap(AuthSpacing.sm)) {
        AuthTextField(
            modifier = Modifier.fillMaxWidth(),
            value = uiState.email,
            onValueChange = { viewModel.onEmailChange(it) },
            placeholder = strings.emailPlaceholder,
            type = InputType.Email,
            leadingIcon = { FaEnvelope(modifier = Modifier.color(colors.secondary)) },
            error = uiState.emailError
        )
    }
}

@Composable
private fun PhoneResetForm(
    viewModel: AuthViewModel,
    otpCooldown: Int,
    contactFilled: Boolean,
    otpSent: Boolean,
    otpComplete: Boolean,
    onSendOtp: () -> Unit,
    colors: AuthColors
) {
    val uiState = viewModel.uiState
    val strings = AppLanguage.strings
    Column(modifier = Modifier.fillMaxWidth().gap(AuthSpacing.sm)) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier.fillMaxWidth().borderRadius(12.px).backgroundColor(colors.inputBg)
                    .border(2.px, LineStyle.Solid, if (uiState.phoneError != null) colors.error else colors.border),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Select(attrs = {
                    style {
                        property("height", "48px"); property("background", "transparent")
                        property("border", "none"); property("border-right", "1.5px solid ${colors.borderLight}")
                        property("outline", "none"); property("color", colors.onBackground.toString())
                        property("font-size", "13px"); property("padding", "0 8px")
                        property("cursor", "pointer"); property("min-width", "100px")
                    }
                }) {
                    Countries.entries.forEach { country -> Option(value = country.code) { Text(country.code) } }
                }
                Input(type = InputType.Tel, attrs = {
                    value(uiState.phone)
                    onInput { viewModel.onPhoneChange(it.value) }
                    attr("placeholder", strings.phonePlaceholder)
                    style {
                        property("background", "transparent"); property("border", "none")
                        property("outline", "none"); property("color", colors.onBackground.toString())
                        property("flex", "1"); property("font-size", "14px")
                        property("padding", "0 14px"); property("height", "48px"); property("width", "100%")
                    }
                })
            }
            uiState.phoneError?.let {
                SpanText(it, modifier = Modifier.color(colors.error).fontSize(AuthTypography.tiny).padding(top = 6.px, left = 12.px))
            }
        }

    }
}

@Composable
private fun ResetOtpField(
    value: String,
    onValueChange: (String) -> Unit,
    error: String?,
    cooldownSeconds: Int,
    canSendOtp: Boolean,
    otpEnabled: Boolean,
    onSendOtp: () -> Unit,
    colors: AuthColors
) {
    val isInCooldown = cooldownSeconds > 0
    val buttonClickable = canSendOtp && !isInCooldown

    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .borderRadius(12.px)
                .border(2.px, LineStyle.Solid, if (error != null) colors.error else colors.border)
                .backgroundColor(colors.inputBg),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Input(type = InputType.Text, attrs = {
                value(value)
                onInput { if (otpEnabled) onValueChange(it.value.filter { c -> c.isDigit() }.take(6)) }
                attr("placeholder", if (otpEnabled) "Code à 6 chiffres" else "Envoie du code ")
                attr("maxlength", "6"); attr("inputmode", "numeric")
                if (!otpEnabled) attr("disabled", "true")
                style {
                    property("background", "transparent"); property("border", "none")
                    property("outline", "none"); property("color", colors.onBackground.toString())
                    property("flex", "1"); property("font-size", "14px")
                    property("padding", "0 14px"); property("height", "48px"); property("width", "100%")
                    if (!otpEnabled) property("cursor", "not-allowed")
                }
            })
            Box(modifier = Modifier.width(1.px).height(32.px).backgroundColor(colors.borderLight))
            Box(
                modifier = Modifier
                    .padding(leftRight = 14.px, topBottom = 12.px)
                    .styleModifier { property("flex-shrink", "0") }
                    .cursor(if (buttonClickable) Cursor.Pointer else Cursor.NotAllowed)
                    .onClick { if (buttonClickable) onSendOtp() },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    when { isInCooldown -> "Renvoyer (${cooldownSeconds}s)"; otpEnabled -> "Renvoyer"; else -> "Envoyer le code" },
                    modifier = Modifier
                        .fontSize(AuthTypography.tiny).fontWeight(FontWeight.SemiBold)
                        .color(if (buttonClickable) colors.primary else colors.mutedText)
                        .styleModifier { property("white-space", "nowrap") }
                )
            }
        }
        error?.let {
            SpanText(it, modifier = Modifier.color(colors.error).fontSize(AuthTypography.tiny).padding(top = 6.px, left = 12.px))
        }
    }
}